<template>
  <div>
    <loading-spinner v-if="loading"> </loading-spinner>
    <template v-else-if="showSent">
      <v-row>
        <v-col cols="auto">
          <v-btn
            text
            @click="showSent = false"
            color="secondary"
            x-large
            class="font-weight-bold"
          >
            <v-icon class="mr-2">
              keyboard_arrow_left
            </v-icon>
            Go Back
          </v-btn>
        </v-col>
      </v-row>
      <v-card>
        <template v-for="(status, i) in statuses">
          <resource-status
            :key="i"
            :resources="
              resources.filter(x => getResourceStatusName(x) == status)
            "
            :status="status"
            v-on:edit="showResourceDialog(resource.id)"
            v-on:delete="deleteResource(resource.id)"
          ></resource-status>
          <v-divider
            :key="i + 'divider'"
            v-if="i < statuses.length - 1"
          ></v-divider>
        </template>
      </v-card>
    </template>
    <template v-else-if="!showOutstandingResources">
      <v-row v-if="outstandingResourceCount > 0">
        <v-col cols="12">
          <single-list-item
            color="performance"
            button-text="View Resources"
            label="Reminder"
            v-on:click="showOutstandingResources = true"
          >
            Your have training resources you haven't viewed
          </single-list-item>
        </v-col>
      </v-row>
      <header-card
        icon="local_library"
        title="Training Resources"
        sub-title="Share articles, videos, audio, and training with your team"
      >
        <template v-slot:right>
          <div class="secondary--text font-weight-medium subtitle-1">
            Total Resources
          </div>
          <div>
            {{ resources.length }}
          </div>
        </template>
        <template v-slot:right2>
          <engagement-text
            title="Participation Rate"
            :engagement="engagement.resources"
            class="subtitle-1"
            click
            v-on:click="
              $router.push({ path: `/reporting/participation/training` })
            "
          ></engagement-text>
        </template>
        <template v-slot:extension>
          <v-divider></v-divider>
          <v-container>
            <v-row>
              <v-col :cols="$vuetify.breakpoint.mdAndUp ? 'auto' : '12'">
                <v-btn
                  to="/reporting/training/overview"
                  color="secondary"
                  block
                >
                  <v-icon class="mr-2">assessment</v-icon>
                  Reporting
                </v-btn>
              </v-col>
              <v-col :cols="$vuetify.breakpoint.mdAndUp ? 'auto' : '12'">
                <v-btn
                  @click="showResourceDialog(null)"
                  color="secondary"
                  block
                >
                  <v-icon class="mr-2">
                    add_circle
                  </v-icon>
                  New Resource
                </v-btn>
              </v-col>
              <v-col :cols="$vuetify.breakpoint.mdAndUp ? 'auto' : '12'">
                <v-btn @click="showSent = true" color="secondary" block>
                  <v-icon class="mr-2">
                    mail
                  </v-icon>
                  View Sent
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </template>
      </header-card>
      <resource-library></resource-library>
    </template>
    <template v-else>
      <v-row>
        <v-col cols="auto">
          <v-btn
            text
            @click="showOutstandingResources = false"
            color="secondary"
            x-large
            class="font-weight-bold"
          >
            <v-icon class="mr-2">
              keyboard_arrow_left
            </v-icon>
            Go Back
          </v-btn>
        </v-col>
      </v-row>
      <resource-queue></resource-queue>
    </template>
    <resource-dialog
      ref="resourceDialog"
      v-on:save="getResources"
    ></resource-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import EngagementText from "@/components/reporting/EngagementText";
import ResourceDialog from "@/components/resources/ResourceDialog";
import ResourceStatus from "@/components/resources/ResourceStatus";
import ResourceQueue from "@/components/resources/ResourceQueue";
import HeaderCard from "@/components/common/HeaderCard";
import ResourceLibrary from "@/components/resources/ResourceLibrary";
import resourceStatuses from "@/helpers/mixins/resourceStatuses";
export default {
  name: "ResourceList",
  components: {
    EngagementText,
    ResourceDialog,
    ResourceStatus,
    ResourceQueue,
    HeaderCard,
    ResourceLibrary
  },
  props: {
    initialFilter: { type: Object },
    initialFilterId: { type: String, default: "" }
  },
  mixins: [resourceStatuses],
  data: () => ({
    loading: true,
    selectedResourceId: "",
    showOutstandingResources: false,
    showSent: false
  }),
  computed: {
    isAdminOrManager() {
      return this.is_admin || this.is_manager;
    },
    outstandingResourceCount() {
      return this.tasks.filter(
        x => x.type == this.$enums.taskTypes.RESOURCE_TO_READ
      ).length;
    },
    statuses() {
      return this._.uniq(
        this.resources.map(x => this.getResourceStatusName(x))
      );
    },
    ...mapGetters({
      resources: "resource/list",
      engagement: "engagement/score",
      core_values: "company/coreValues",
      profile: "profile/profile",
      tasks: "task/tasks",
      is_admin: "profile/is_admin",
      is_manager: "profile/is_manager"
    })
  },
  created() {
    this.filterId = this.initialFilterId;
    this.$store.dispatch("company/getCoreValues");
    this.getResources();
  },
  methods: {
    getResources() {
      Promise.all([
        this.$store.dispatch("resource/getResources"),
        this.$store.dispatch("pulse/getQuestions"),
        this.$store.dispatch("engagement/getEngagement", {
          type: this.$coreScores.SURVEY
        })
      ])
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading your training resources."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    showResourceDialog(resourceId) {
      this.selectedResourceId = resourceId;
      this.$refs.resourceDialog.showDialog(resourceId);
    },
    async deleteResource(id) {
      if (
        await this.$root.$confirm(
          "Delete resource",
          "Are you sure you want to delete this training resource?",
          { color: "red" }
        )
      ) {
        this.$store
          .dispatch("resource/deleteResource", id)
          .then(() => {
            this.$emit("delete");
            this.$root.$snackbarSuccess("Training resource deleted");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occured while deleting this training resource."
            );
          });
      }
    }
  }
};
</script>
